import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Avatar, AvatarImage, AvatarFallback } from "./ui/avatar";

const API = "/api";

const ROLE_COLORS = {
  mentor: { border: "#22c55e", bg: "rgba(34,197,94,0.12)", label: "Mentor" },
  mentee: { border: "#86efac", bg: "rgba(134,239,172,0.12)", label: "Mentee" },
  advisor: { border: "#0d9488", bg: "rgba(13,148,136,0.12)", label: "Advisor" },
  advising: { border: "#5eead4", bg: "rgba(94,234,212,0.12)", label: "Advising" },
  professor: { border: "#059669", bg: "rgba(5,150,105,0.12)", label: "Professor" },
  student: { border: "#0ea5e9", bg: "rgba(14,165,233,0.12)", label: "Student" },
  coach: { border: "#eab308", bg: "rgba(234,179,8,0.12)", label: "Coach" },
  coachee: { border: "#f97316", bg: "rgba(249,115,22,0.12)", label: "Coachee" },
  therapist: { border: "#ec4899", bg: "rgba(236,72,153,0.12)", label: "Therapist" },
  coworker: { border: "#8b5cf6", bg: "rgba(139,92,246,0.12)", label: "Coworker" },
  business_partner: { border: "#6366f1", bg: "rgba(99,102,241,0.12)", label: "Business Partner" },
  friend: { border: "#3b82f6", bg: "rgba(59,130,246,0.12)", label: "Friend" },
  family: { border: "#e11d48", bg: "rgba(225,29,72,0.12)", label: "Family" },
  other: { border: "#94a3b8", bg: "rgba(148,163,184,0.12)", label: "Other" },
};

/* ── Assign nodes to orbits based on insight count ── */
function assignOrbits(nodes) {
  if (nodes.length === 0) return [];

  const sorted = [...nodes].map((n, origIdx) => ({ ...n, _origIdx: origIdx }));
  sorted.sort((a, b) => (b.insight_count || 0) - (a.insight_count || 0));

  const orbits = [[], [], []]; // inner, mid, outer
  sorted.forEach((node, i) => {
    if (node.status === "pending") {
      orbits[2].push(node); // pending always outer
    } else if (i < Math.ceil(sorted.length / 3)) {
      orbits[0].push(node);
    } else if (i < Math.ceil((sorted.length * 2) / 3)) {
      orbits[1].push(node);
    } else {
      orbits[2].push(node);
    }
  });

  // Assign even starting angles per orbit with parallax depth
  const result = [];
  orbits.forEach((ring, orbitIdx) => {
    ring.forEach((node, i) => {
      const angleStep = 360 / Math.max(ring.length, 1);
      // Parallax: inner fastest/largest, outer slowest/dimmest
      const speedByOrbit = [0.09, 0.055, 0.032]; // deg per tick
      const depthOpacity = [1, 0.92, 0.82];
      // Slight eccentricity per node so orbits don't feel synthetic
      const eccentricity = 0.08 + (i % 3) * 0.04;
      result.push({
        ...node,
        orbit: orbitIdx,
        startAngle: i * angleStep + orbitIdx * 40,
        speed: speedByOrbit[orbitIdx],
        depthOpacity: depthOpacity[orbitIdx],
        eccentricity,
        wobbleSeed: (orbitIdx * 100 + i * 37) % 360, // unique phase per node
      });
    });
  });

  return result;
}

const NetworkMindMap = ({ onNodeClick, onStatsChange, snapshotRef, externalData, readOnly = false }) => {
  const [data, setData] = useState(externalData || null);
  const [loading, setLoading] = useState(!externalData);
  const [hovered, setHovered] = useState(null);
  const [tick, setTick] = useState(0);
  const internalRef = useRef(null);
  const containerRef = snapshotRef || internalRef;
  const [dims, setDims] = useState({ w: 800, h: 600 });

  const fetchData = useCallback(async () => {
    if (externalData) return;
    try {
      const res = await fetch(`${API}/network/mindmap`, { credentials: "include" });
      if (res.ok) {
        const d = await res.json();
        setData(d);
        if (d?.stats) onStatsChange?.(d.stats);
      }
    } catch {} finally { setLoading(false); }
  }, [onStatsChange, externalData]);

  // Keep in sync with externalData prop changes
  useEffect(() => {
    if (externalData) {
      setData(externalData);
      setLoading(false);
      if (externalData.stats) onStatsChange?.(externalData.stats);
    }
  }, [externalData, onStatsChange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Animation — requestAnimationFrame (pauses in background tabs, smoother than setInterval)
  useEffect(() => {
    let raf;
    let lastTs = 0;
    const TICK_MS = 50; // match previous cadence
    const step = (ts) => {
      if (!lastTs) lastTs = ts;
      if (ts - lastTs >= TICK_MS) {
        setTick((t) => t + 1);
        lastTs = ts;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Responsive dimensions — actively track container size via ResizeObserver
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const updateDims = () => {
      const rect = el.getBoundingClientRect();
      setDims({ w: rect.width, h: rect.height });
    };
    updateDims();
    const ro = new ResizeObserver(updateDims);
    ro.observe(el);
    return () => ro.disconnect();
  }, [data, containerRef]);

  const orbitNodes = useMemo(() => {
    if (!data || data.nodes.length === 0) return [];
    return assignOrbits(data.nodes);
  }, [data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data || data.nodes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-center" data-testid="mindmap-empty">
        <p className="text-slate-500 text-sm mb-2">Your network mind map is empty.</p>
        <p className="text-xs text-slate-400">Invite people to see your connections here.</p>
      </div>
    );
  }

  const { center } = data;
  const cx = dims.w / 2;
  const cy = dims.h / 2;
  // Center sizing scales with viewport (smaller on mobile, larger on big screens)
  const centerSize = Math.max(38, Math.min(60, dims.w * 0.055));

  // Node sizing: shrink when we have lots of connections to avoid overlap
  const count = orbitNodes.length;
  const nodeSize = count > 18 ? 18 : count > 12 ? 22 : count > 8 ? 26 : 30;

  // Orbit radii — fill the container. Leave padding for node diameter + label height.
  const labelSpace = 30; // room for name + role below each node
  const sidePadding = nodeSize + 8;
  const topBottomPadding = nodeSize + labelSpace;
  const maxRx = Math.max(80, dims.w / 2 - sidePadding);
  const maxRy = Math.max(60, dims.h / 2 - topBottomPadding);
  // Three orbit rings: innermost leaves generous breathing room around center; outer hits the edge.
  const innermost = Math.max(
    centerSize + nodeSize + 50,
    Math.min(maxRx, maxRy) * 0.42
  );
  const orbitRadii = [
    {
      rx: innermost,
      ry: innermost * 0.72,
      rotation: -20,
    },
    {
      rx: Math.max(innermost + nodeSize * 2 + 30, maxRx * 0.7),
      ry: Math.max(innermost * 0.72 + nodeSize * 2 + 20, maxRy * 0.7),
      rotation: 15,
    },
    {
      rx: maxRx,
      ry: maxRy,
      rotation: -8,
    },
  ];

  // Per-orbit slow wobble — makes each ring "breathe" organically
  const t = tick;
  const wobble = (period, amp) => Math.sin((t / period) * Math.PI * 2) * amp;
  const orbitWobble = orbitRadii.map((o, i) => ({
    ...o,
    // Independent breathing cycles per ring so they never sync up
    rxMod: 1 + wobble(160 + i * 47, 0.04),
    ryMod: 1 + wobble(200 + i * 53, 0.035),
    rotDrift: wobble(280 + i * 61, 5), // degrees
  }));

  // Calculate positions — Kepler-ish acceleration near the near side
  const positions = orbitNodes.map(node => {
    const orbit = orbitWobble[node.orbit];
    const baseDeg = node.startAngle + tick * node.speed;
    const baseRad = (baseDeg * Math.PI) / 180;

    // Eccentric angular sweep: speeds up near the "near" half, slows on "far" half.
    // Warp: angle = base + k * sin(base). Acts like a smooth easing.
    const k = node.eccentricity || 0.12;
    const warped = baseRad + k * Math.sin(baseRad);

    // Subtle per-node radius breathing (adds to the orbit-level wobble)
    const nodeWobble = Math.sin((tick / 90 + node.wobbleSeed) * Math.PI / 180) * 0.025;
    const rx = orbit.rx * orbit.rxMod * (1 + nodeWobble);
    const ry = orbit.ry * orbit.ryMod * (1 - nodeWobble);

    const rot = ((orbit.rotation + orbit.rotDrift) * Math.PI) / 180;
    const ex = rx * Math.cos(warped);
    const ey = ry * Math.sin(warped);
    const x = cx + ex * Math.cos(rot) - ey * Math.sin(rot);
    const y = cy + ex * Math.sin(rot) + ey * Math.cos(rot);

    return { ...node, x, y };
  });

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden"
      style={{
        minHeight: 520,
        height: "min(860px, calc(100vh - 180px))",
        background: "transparent",
      }}
      data-testid="network-mindmap"
    >
      {/* SVG layer: orbit rings + connection lines */}
      <svg
        width={dims.w}
        height={dims.h}
        className="absolute inset-0"
        style={{ pointerEvents: "none" }}
      >
        <defs>
          <radialGradient id="center-glow-mm" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.08" />
            <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Breathing center glow — slow pulse 1→1.08→1 */}
        <circle
          cx={cx}
          cy={cy}
          r={(centerSize + 40) * (1 + Math.sin((tick / 140) * Math.PI * 2) * 0.06)}
          fill="url(#center-glow-mm)"
        />

        {/* Dotted orbit ellipses — follow the same wobble as nodes */}
        {orbitWobble.map((orbit, i) => (
          <ellipse
            key={`orbit-${i}`}
            cx={cx} cy={cy}
            rx={orbit.rx * orbit.rxMod} ry={orbit.ry * orbit.ryMod}
            fill="none"
            stroke="#8b5cf6"
            strokeOpacity={0.28 - i * 0.06}
            strokeWidth="1.25"
            strokeDasharray="4 8"
            transform={`rotate(${orbit.rotation + orbit.rotDrift}, ${cx}, ${cy})`}
          />
        ))}

        {/* Connection lines from center to each node */}
        {positions.map((node, i) => {
          const roleInfo = ROLE_COLORS[node.role] || ROLE_COLORS.other;
          const isPending = node.status === "pending";
          const isHov = hovered === i;

          return (
            <line
              key={`line-${i}`}
              x1={cx} y1={cy}
              x2={node.x} y2={node.y}
              stroke={isPending ? "#cbd5e1" : roleInfo.border}
              strokeWidth={isPending ? 0.5 : isHov ? 1.5 : 0.8}
              strokeOpacity={isPending ? 0.15 : isHov ? 0.4 : 0.12}
              strokeDasharray={isPending ? "3,4" : "none"}
              style={{ transition: "stroke-opacity 0.3s, stroke-width 0.3s" }}
            />
          );
        })}
      </svg>

      {/* Center node (current user) */}
      {(() => {
        const pulse = 1 + Math.sin((tick / 140) * Math.PI * 2) * 0.04;
        return (
        <div
          className="absolute flex flex-col items-center z-20"
          style={{
            left: cx - centerSize,
            top: cy - centerSize,
            width: centerSize * 2,
          }}
        >
          <div
            className="rounded-full overflow-hidden"
            style={{
              width: centerSize * 2,
              height: centerSize * 2,
              border: "3px solid #8b5cf6",
              boxShadow: `0 0 0 ${4 * pulse}px rgba(139,92,246,${0.14 * pulse}), 0 4px ${24 * pulse}px rgba(139,92,246,${0.18 * pulse})`,
              transform: `scale(${pulse})`,
              transition: "transform 0.15s ease-out",
            }}
          >
            <Avatar className="w-full h-full">
              <AvatarImage src={center.picture} alt={center.name} />
              <AvatarFallback className="bg-violet-100 text-violet-600 text-2xl font-bold">
                {center.name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
          </div>
          <span className="text-xs font-semibold text-slate-700 mt-1.5 bg-white/90 px-2.5 py-0.5 rounded-full shadow-sm border border-slate-100">
            You
          </span>
        </div>
        );
      })()}

      {/* Orbiting connection nodes */}
      {positions.map((node, i) => {
        const roleInfo = ROLE_COLORS[node.role] || ROLE_COLORS.other;
        const isPending = node.status === "pending";
        const isHov = hovered === i;
        const firstName = node.name?.split(" ")[0] || "?";
        const isRecentActive = node.last_insight_at
          ? (Date.now() - new Date(node.last_insight_at).getTime()) < 7 * 86400000
          : false;

        return (
          <div
            key={`node-${node.connection_id}`}
            className={`absolute flex flex-col items-center ${isPending ? "opacity-40" : (readOnly ? "" : "cursor-pointer")}`}
            style={{
              left: node.x - nodeSize,
              top: node.y - nodeSize,
              width: nodeSize * 2,
              transition: "transform 0.2s, opacity 0.3s",
              transform: isHov && !isPending ? "scale(1.15)" : "scale(1)",
              zIndex: isHov ? 50 : 10,
              opacity: isPending ? 0.4 : (node.depthOpacity ?? 1),
            }}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
            onClick={() => !isPending && !readOnly && onNodeClick?.(node)}
            data-testid={`mindmap-node-${node.connection_id}`}
          >
            {/* Avatar */}
            <div
              className={`rounded-full overflow-hidden flex-shrink-0 relative ${isRecentActive && !isPending ? "mindmap-pulse" : ""}`}
              style={{
                width: nodeSize * 2,
                height: nodeSize * 2,
                border: `2.5px solid ${isPending ? "#cbd5e1" : roleInfo.border}`,
                filter: isPending ? "grayscale(0.5)" : "none",
                boxShadow: isHov
                  ? `0 0 0 3px ${roleInfo.border}33, 0 4px 16px ${roleInfo.border}22`
                  : "0 2px 8px rgba(0,0,0,0.06)",
                transition: "box-shadow 0.25s",
                ["--pulse-color"]: roleInfo.border,
              }}
            >
              <Avatar className="w-full h-full">
                <AvatarImage src={node.picture} alt={node.name} />
                <AvatarFallback
                  className="text-sm font-semibold"
                  style={{ backgroundColor: roleInfo.bg, color: roleInfo.border }}
                >
                  {node.name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
            </div>

            {/* Name + role label */}
            <div className="flex flex-col items-center mt-1.5 pointer-events-none" style={{ width: "max-content", maxWidth: 100 }}>
              <span className="text-[10px] font-semibold text-slate-700 leading-tight text-center truncate max-w-[100px]">
                {firstName}
              </span>
              <span
                className="hidden sm:inline-block text-[8px] font-medium mt-0.5 px-1.5 py-px rounded-full capitalize"
                style={{
                  color: isPending ? "#94a3b8" : roleInfo.border,
                  backgroundColor: isPending ? "rgba(148,163,184,0.1)" : `${roleInfo.border}14`,
                }}
              >
                {isPending ? "Pending" : roleInfo.label}
              </span>
            </div>

            {/* Tooltip on hover */}
            {isHov && !isPending && (
              <div
                className="absolute bg-slate-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl whitespace-nowrap z-30 pointer-events-none"
                style={{
                  bottom: nodeSize * 2 + 16,
                  left: "50%",
                  transform: "translateX(-50%)",
                }}
                data-testid={`mindmap-tooltip-${i}`}
              >
                <p className="font-semibold">{node.name}</p>
                <p style={{ color: roleInfo.border }} className="capitalize">{roleInfo.label}</p>
                <p className="text-slate-400 mt-0.5">
                  {node.insight_count === 0
                    ? "No insights exchanged yet"
                    : `${node.insight_count} insight${node.insight_count > 1 ? "s" : ""} exchanged`}
                </p>
                <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 bg-slate-900 rotate-45" />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default NetworkMindMap;
